'use client';

import { createStore } from 'zustand';

import { getLogger } from '@/logger/logger';
import { getActiveCart } from '@/services/cart/get-active-cart.service';
import { Cart } from '@/types/cart.types';
import { CartOrigin } from '@/types/enums/analytics/cart/cart-analytics.enum';
import { trackCartOpened } from '@/utils/cart/cart-analytics.util';

const logger = getLogger('cart-store');

export type CartFetchStatus = 'idle' | 'loading' | 'loaded' | 'error';

export type CartState = {
  isOpen: boolean;
  cart: Cart | null;
  status: CartFetchStatus;
  origin: CartOrigin;
};

const initialState: CartState = {
  isOpen: false,
  cart: null,
  status: 'idle',
  origin: CartOrigin.OTHER,
};

export const cartStore = createStore<CartState>(() => ({ ...initialState }));

let pendingLoad: Promise<void> | null = null;
let pendingLoadAbort: AbortController | null = null;
let pendingLoadIsDrawerOwned = false;

const abortPendingLoad = () => {
  if (!pendingLoad) {
    return;
  }

  pendingLoadAbort?.abort();
  pendingLoad = null;
  pendingLoadAbort = null;
  pendingLoadIsDrawerOwned = false;

  const { status, cart } = cartStore.getState();

  if (status === 'loading') {
    cartStore.setState({ status: cart ? 'loaded' : 'idle' });
  }
};

const abortDrawerLoad = () => {
  if (!pendingLoadIsDrawerOwned) {
    return;
  }

  abortPendingLoad();
};

const startLoad = (isDrawerOwned: boolean): Promise<void> => {
  if (pendingLoad) {
    return pendingLoad;
  }

  const abortController = new AbortController();
  const { signal } = abortController;

  pendingLoadAbort = abortController;
  pendingLoadIsDrawerOwned = isDrawerOwned;

  if (cartStore.getState().status !== 'loaded') {
    cartStore.setState({ status: 'loading' });
  }

  const load = getActiveCart(signal)
    .then((cart) => {
      if (signal.aborted) {
        return;
      }

      cartStore.setState({ cart, status: 'loaded' });

      if (cartStore.getState().isOpen) {
        trackCartOpened(cartStore.getState().origin, cart?.items ?? []);
      }
    })
    .catch((error) => {
      if (signal.aborted) {
        return;
      }

      logger.error('Failed to fetch the active cart', error);
      cartStore.setState({ status: 'error' });
    })
    .finally(() => {
      if (pendingLoad === load) {
        pendingLoad = null;
        pendingLoadAbort = null;
        pendingLoadIsDrawerOwned = false;
      }
    });

  pendingLoad = load;

  return load;
};

export const loadCart = (): Promise<void> => startLoad(false);

export const openCart = (origin: CartOrigin) => {
  cartStore.setState({ isOpen: true, origin });

  void startLoad(true);
};

export const retryCart = (): Promise<void> => {
  cartStore.setState({ isOpen: true });

  return startLoad(true);
};

export const closeCart = () => {
  abortDrawerLoad();
  cartStore.setState({ isOpen: false });
};

export const setCartOpen = (isOpen: boolean, origin: CartOrigin = CartOrigin.OTHER) => {
  if (isOpen) {
    openCart(origin);
    return;
  }

  closeCart();
};

export const resetCart = () => {
  abortPendingLoad();
  cartStore.setState({ ...initialState });
};
